/**
 * 时间表（与 iOS Apps/Parent/Sources/Schedule/ScheduleView 一致）：日 / 周 / 月视图、新建和编辑日程（含提醒）。
 * 时间表只有日程一种（docs/API.md 2.4），点哪一项都打开日程编辑。
 * 调试参数：?dev_sheet=week|month 打开周 / 月视图；edit（有提醒的日程）、edit_allday、edit_multiday 直接打开编辑器。
 */
import { CalendarDays, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { dataStore, devSheet, syncSoon, takeDevSheet, toast, useCalendar, useCurrentChild, useStore, type ScheduleEvent } from "../../core";
import { ChildSwitcher, EmptyHint, Page, PageTitle, PillSegment } from "../../ui";
import { EventEditor, type EditTarget } from "./EventEditor";
import { DayView, MonthView, WeekView } from "./views";
import "./schedule.css";

type Mode = "day" | "week" | "month";

const MODES = [
  ["day", "日"],
  ["week", "周"],
  ["month", "月"],
] as const;

/** 调试：直接打开某类日程的编辑器 */
const DEV_EDIT: Record<string, (e: ScheduleEvent, hasRule: boolean) => boolean> = {
  edit: (_e, hasRule) => hasRule,
  edit_allday: (e) => e.allDay === true,
  edit_multiday: (e) => e.allDay !== true && e.durationMin > 1440,
};

export function SchedulePage() {
  const cal = useCalendar();
  const child = useCurrentChild();
  const [mode, setMode] = useState<Mode>(() => (takeDevSheet("week") ? "week" : takeDevSheet("month") ? "month" : "day"));
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<(EditTarget & { key: number }) | null>(null);
  const date = selected ?? cal.today();

  const open = (eventId: string | null, d: string) => {
    // 点的是已有日程但本地还没同步到：不要打开成空白的"新建日程"
    if (eventId && !dataStore.getState().scheduleEvents[eventId]) {
      syncSoon();
      toast("这一项还没同步到，稍后再点一次", { error: true });
      return;
    }
    setEditing({ eventId, date: d, key: Date.now() });
  };
  const pickDay = (d: string) => {
    setSelected(d);
    setMode("day");
  };

  useDevEditor(child?.id ?? null, (e) => open(e.id, e.startDate));

  return (
    <Page gap={14}>
      <PageTitle
        trailing={
          <button type="button" className="sch-add" aria-label="新建日程" title="新建日程（上课、作业、喝水、学习内容、打开其它 App 都是日程）" disabled={!child} onClick={() => open(null, date)}>
            <Plus size={22} strokeWidth={3} />
          </button>
        }
      >
        时间表
      </PageTitle>
      <div className="sch-toolbar">
        <ChildSwitcher />
        <PillSegment items={MODES} value={mode} onChange={setMode} />
      </div>

      {child ? (
        mode === "day" ? (
          <DayView child={child} date={date} onSelect={setSelected} onEdit={open} />
        ) : mode === "week" ? (
          <WeekView child={child} date={date} onSelect={setSelected} onPickDay={pickDay} onEdit={open} />
        ) : (
          <MonthView child={child} date={date} onSelect={setSelected} onPickDay={pickDay} />
        )
      ) : (
        <EmptyHint icon={CalendarDays} text="先在「我的」里添加孩子" />
      )}

      {child && editing && <EventEditor key={editing.key} child={child} target={editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}

/** ?dev_sheet=edit|edit_allday|edit_multiday：数据同步下来后打开第一个符合条件的日程 */
function useDevEditor(childId: string | null, onOpen: (e: ScheduleEvent) => void): void {
  const key = devSheet();
  const match = key ? DEV_EDIT[key] : undefined;
  const target = useStore((s) => {
    if (!match || !childId) return undefined;
    const rules = Object.values(s.reminderRules);
    return Object.values(s.scheduleEvents)
      .filter((e) => e.childId === childId && match(e, rules.some((r) => r.sourceType === "event" && r.sourceId === e.id)))
      .sort((a, b) => b.startTime.localeCompare(a.startTime))[0];
  });
  useEffect(() => {
    if (target && key && takeDevSheet(key)) onOpen(target);
  }, [target, key]);
}
